import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// Local (timezone-safe) YYYY-MM-DD key for a Date
const formatLocalDateKey = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Parse a date/datetime string as LOCAL midnight. `new Date('YYYY-MM-DD')` parses as
// UTC and shifts a day behind in negative timezones, so we extract the parts instead.
const parseLocalDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }
    const str = String(value).trim();
    const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const DailyCareTab = forwardRef(({ inpatientId, visitDate, checkoutDate, visitStatus, readOnly = false }, ref) => {
    const { t } = useTranslation();
    const [selectedDay, setSelectedDay] = useState(1);
    const [days, setDays] = useState([]);

    // Only 'active' visits keep growing a tab per day; any other status is frozen.
    // A missing status defaults to open so a partial payload can't lock a live visit.
    const isVisitOpen = visitStatus ? String(visitStatus).toLowerCase() === 'active' : true;
    const frozenDaysCountRef = useRef(null);

    // Today's date key, refreshed periodically so tabs and edit-lock roll over
    // when the calendar day changes while the modal is open.
    const [todayKey, setTodayKey] = useState(() => formatLocalDateKey(new Date()));
    const todayKeyRef = useRef(todayKey);

    // State for daily records
    const [dailyRecords, setDailyRecords] = useState({});
    const [loading, setLoading] = useState(false);

    // Refs for safe async operations and cleanup
    const isMounted = useRef(true);
    const dailyRecordsRef = useRef(dailyRecords);
    const saveTimersRef = useRef({});
    const selectedDayRef = useRef(selectedDay);
    const savingDaysRef = useRef(new Set()); // Track which days are currently being saved

    useEffect(() => {
        selectedDayRef.current = selectedDay;
    }, [selectedDay]);

    // Keep ref string with state
    useEffect(() => {
        dailyRecordsRef.current = dailyRecords;
    }, [dailyRecords]);

    useEffect(() => {
        todayKeyRef.current = todayKey;
    }, [todayKey]);

    useEffect(() => {
        const timer = setInterval(() => {
            const next = formatLocalDateKey(new Date());
            setTodayKey(prev => (prev === next ? prev : next));
        }, 30000);
        return () => clearInterval(timer);
    }, []);

    const saveDailyRecord = useCallback(async (day, recordData, isUnmounting = false) => {
        if (readOnly || !isVisitOpen || !inpatientId || !visitDate) return;

        // Don't save if recordData is empty or undefined
        if (!recordData) return;

        // Prevent duplicate saves for the same day
        if (savingDaysRef.current.has(day)) return;

        // Check if there's at least one field with data
        const hasData = Object.values(recordData).some(val => 
            val !== null && val !== undefined && val !== '' && String(val).trim() !== ''
        );
        
        if (!hasData) return;

        // Mark this day as being saved
        savingDaysRef.current.add(day);

        try {
            // Parse visit date as local YYYY-MM-DD to avoid timezone shifts
            const start = getLocalVisitDate();
            if (!start) return;
            const targetDate = new Date(start.getFullYear(), start.getMonth(), start.getDate());
            targetDate.setDate(targetDate.getDate() + (day - 1));
            const y = targetDate.getFullYear();
            const m = String(targetDate.getMonth() + 1).padStart(2, '0');
            const d = String(targetDate.getDate()).padStart(2, '0');
            const dateStr = `${y}-${m}-${d}`;

            // Day-lock: never CREATE a record for a day other than today (past days are
            // locked once their day is done). Updating an existing record (PUT) is allowed.
            if (dateStr !== todayKeyRef.current && !recordData.id) return;
            
            // For PUT requests, if we don't have serial, fetch it from the API first
            let serialToUse = recordData.serial;
            
            // Check if we need to fetch serial (for PUT requests without serial)
            const needsSerialFetch = recordData.id && (!serialToUse || serialToUse === '' || serialToUse === null || serialToUse === undefined);
            
            if (needsSerialFetch) {
                // Try to get serial from ref first
                const refRecord = dailyRecordsRef.current[day];
                if (refRecord && refRecord.serial && refRecord.serial !== '' && refRecord.serial !== null && refRecord.serial !== undefined) {
                    serialToUse = refRecord.serial;
                } else {
                    // Fetch from API if not in ref (CRITICAL for PUT - backend requires serial)
                    try {
                        const fetchResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/vital-signs/${recordData.id}`, {
                            headers: {
                                'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                                'Accept': 'application/json'
                            }
                        });
                        
                        if (fetchResponse.ok) {
                            const fetchResult = await fetchResponse.json();
                            const fetchedSerial = fetchResult.data?.serial || fetchResult.serial || '';
                            if (fetchedSerial && fetchedSerial !== '') {
                                serialToUse = String(fetchedSerial);
                                // Update ref with the fetched serial
                                dailyRecordsRef.current = {
                                    ...dailyRecordsRef.current,
                                    [day]: {
                                        ...dailyRecordsRef.current[day],
                                        serial: serialToUse
                                    }
                                };
                            }
                        }
                    } catch (fetchErr) {
                        // ignore fetch errors for serial lookup
                    }
                }
            }
            
            // Final check: if we still don't have serial for PUT, this is a critical problem
            if (recordData.id && (!serialToUse || serialToUse === '')) {}
            
            const serialString = (serialToUse === null || serialToUse === undefined || serialToUse === '') 
                ? '' 
                : String(serialToUse);
            
            const payload = {
                inpatient_visit_id: parseInt(inpatientId),
                date: dateStr,
                serial: serialString, // Always a string, never null
                // Format temperature, heart_rate, respiratory_rate with units if they have values
                temperature: recordData.temp ? `${parseFloat(recordData.temp)}°C` : '',
                heart_rate: recordData.pulse ? `${parseInt(recordData.pulse)} bpm` : '',
                respiratory_rate: recordData.rr ? `${parseInt(recordData.rr)} breaths/min` : '',
                capillary_refill_time: recordData.crt || '',
                body_weight: recordData.weight ? parseFloat(recordData.weight) : 0,
                mucous_membrane_color: recordData.mm || '',
                body_condition_score: recordData.bcs || '',
                hydration_status: recordData.hydration || '',
                pain_scale: recordData.painScore ? parseInt(recordData.painScore) : 0,
                // Additional fields for daily care
                food_intake: recordData.foodIntake || '',
                appetite: recordData.appetite || '',
                water: recordData.water || '',
                stool: recordData.stool || '',
                urine: recordData.urine || '',
                oral_cavity: recordData.oralCavity || '',
                lymph_nodes: recordData.lymphNodes || '',
                // Use 'severity' for pain score if API expects it, or keep pain_scale
                severity: recordData.painScore || null,
                objective: recordData.physicalExamFindings || ''
            };

            let url = `${import.meta.env.VITE_API_URL}/api/v1/vital-signs`;
            let method = 'POST';

            if (recordData.id) {
                url = `${url}/${recordData.id}`;
                method = 'PUT';
            }

            // Double-check serial is a string before sending
            if (typeof payload.serial !== 'string') {
                payload.serial = String(payload.serial || '');
            }

            const response = await fetch(url, {
                method: method,
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(payload),
                keepalive: true
            });

            if (response.ok && !isUnmounting && isMounted.current) {
                const result = await response.json();
                
                // Handle both response structures: result.data.id or result.id
                const newId = result.data?.id || result.id || result.data?.data?.id;
                
                // IMPORTANT: Store the date we sent along with the ID so we can match records later
                // The backend might not return the date field, so we need to track it ourselves
                const savedDate = dateStr; // This is the date we sent in the payload
                
                if (method === 'POST' && newId) {
                    // Get the serial from the response (backend generates it)
                    const responseSerial = result.data?.serial || result.serial || '';
                    
                    // Update state with the new ID, serial, and date from backend
                    setDailyRecords(prev => {
                        const updated = {
                            ...prev,
                            [day]: {
                                ...(prev[day] || {}),
                                ...recordData,
                                id: newId,
                                serial: responseSerial || recordData.serial || '', // Use backend serial if available
                                _savedDate: savedDate // Store the date we sent so we can match records later
                            }
                        };
                        // Immediately update ref to ensure it's available for next save
                        dailyRecordsRef.current = {
                            ...dailyRecordsRef.current,
                            [day]: {
                                ...dailyRecordsRef.current[day],
                                ...recordData,
                                id: newId,
                                serial: responseSerial || recordData.serial || '',
                                _savedDate: savedDate
                            }
                        };
                        return updated;
                    });
                } else if (method === 'PUT') {
                    // Update was successful - ensure ref has the latest data including serial and date
                    const responseSerial = result.data?.serial || result.serial;
                    dailyRecordsRef.current = {
                        ...dailyRecordsRef.current,
                        [day]: {
                            ...dailyRecordsRef.current[day],
                            ...recordData,
                            serial: responseSerial || dailyRecordsRef.current[day]?.serial || recordData.serial || '',
                            _savedDate: savedDate // Preserve the date we sent
                        }
                    };
                    // Also update state to keep it in sync
                    setDailyRecords(prev => ({
                        ...prev,
                        [day]: {
                            ...prev[day],
                            ...recordData,
                            serial: responseSerial || prev[day]?.serial || recordData.serial || '',
                            _savedDate: savedDate
                        }
                    }));
                } else if (method === 'POST' && !newId) {
                    // no-op if no ID returned
                }
            }
        } catch (error) {
            // ignore
        } finally {
            // Always remove from saving set, even if there was an error
            savingDaysRef.current.delete(day);
        }
    }, [inpatientId, visitDate, isVisitOpen]);

    // Save previous day's data when switching days to ensure nothing is lost
    // This must be after saveDailyRecord is defined
    const prevSelectedDayRef = useRef(selectedDay);
    useEffect(() => {
        const prevDay = prevSelectedDayRef.current;
        // If we switched days and the previous day had data, save it
        if (prevDay !== selectedDay && prevDay > 0) {
            const prevDayData = dailyRecordsRef.current[prevDay];
            if (prevDayData) {
                // Check if there's data to save
                const hasData = Object.entries(prevDayData).some(([key, val]) => 
                    key !== 'id' && val !== null && val !== undefined && val !== '' && String(val).trim() !== ''
                );
                if (hasData) {
                    // Clear any pending timer for the previous day
                    if (saveTimersRef.current[prevDay]) {
                        clearTimeout(saveTimersRef.current[prevDay]);
                        delete saveTimersRef.current[prevDay];
                    }
                    // Save the previous day's data
                    saveDailyRecord(prevDay, prevDayData);
                }
            }
        }
        prevSelectedDayRef.current = selectedDay;
    }, [selectedDay, saveDailyRecord]);

    useImperativeHandle(ref, () => ({
        save: async () => {
            // Clear any pending timers
            Object.keys(saveTimersRef.current).forEach(day => {
                const timer = saveTimersRef.current[day];
                if (timer) {
                    clearTimeout(timer);
                    delete saveTimersRef.current[day];
                }
            });

            // Get current records from ref
            const recordsToSave = dailyRecordsRef.current;
            
            const savePromises = [];
            
            // Save all days that have data
            for (const [day, recordData] of Object.entries(recordsToSave)) {
                if (recordData) {
                    // Check if there's at least one non-empty field (excluding 'id' and 'serial')
                    const hasData = Object.entries(recordData).some(([key, val]) => 
                        key !== 'id' && key !== 'serial' && val !== null && val !== undefined && val !== '' && String(val).trim() !== ''
                    );
                    if (hasData) {
                        savePromises.push(saveDailyRecord(parseInt(day), recordData));
                    }
                }
            }
            
            // Also ensure current day is saved
            const currentDay = selectedDayRef.current;
            const currentRecord = dailyRecordsRef.current[currentDay];
            if (currentRecord) {
                const hasData = Object.entries(currentRecord).some(([key, val]) => 
                    key !== 'id' && key !== 'serial' && val !== null && val !== undefined && val !== '' && String(val).trim() !== ''
                );
                if (hasData) {
                    // Check if we already added this day
                    const dayStr = String(currentDay);
                    if (!recordsToSave[dayStr] || recordsToSave[dayStr] !== currentRecord) {
                        savePromises.push(saveDailyRecord(currentDay, currentRecord));
                    }
                }
            }
            
            if (savePromises.length > 0) {
                await Promise.all(savePromises);
                
                // Refetch data after save to ensure we have the latest from backend
                // This ensures data persists when switching tabs or closing modal
                if (fetchVitalSignsRef.current) {
                    await fetchVitalSignsRef.current();
                }
            }
        }
    }));

    useEffect(() => {
        return () => {
            isMounted.current = false;
            // On unmount, if there are pending timers, save immediately
            Object.keys(saveTimersRef.current).forEach(day => {
                const timer = saveTimersRef.current[day];
                if (timer) {
                    clearTimeout(timer);
                    // Fire and forget save for the pending record
                    const recordToSave = dailyRecordsRef.current[day];
                    if (recordToSave) {
                        saveDailyRecord(day, recordToSave, true); // true = isUnmounting
                    }
                }
            });
        };
    }, [saveDailyRecord]);

    const processVitalSigns = useCallback((records) => {
        const processed = {};
        const dayRecords = {}; // Track all records per day to find the most recent
        
        if (!visitDate) return;
        const startDate = parseLocalDate(visitDate);
        if (!startDate) return;

        // First pass: group records by day and log date calculations
        records.forEach(record => {
            // Try multiple possible date field names (backend might use different field names)
            // The 'date' field is what we send when saving, but backend might return it differently
            const recordDateStr = record.date ||
                                  record.vital_sign_date ||
                                  record.record_date ||
                                  record.date_of_record ||
                                  record.created_at; // Fallback to created_at if no date field

            const recordDate = parseLocalDate(recordDateStr);
            if (!recordDate) return;

            const diffTime = recordDate - startDate;
            const dayOffset = Math.floor(diffTime / (1000 * 60 * 60 * 24));
            const dayNumber = dayOffset + 1;

            if (dayNumber > 0) {
                if (!dayRecords[dayNumber]) {
                    dayRecords[dayNumber] = [];
                }
                dayRecords[dayNumber].push(record);
            }
        });

        // Second pass: for each day, try to match by saved date first, then use most recent
        // First, collect saved dates from our ref
        const savedDatesByDay = {};
        Object.keys(dailyRecordsRef.current).forEach(day => {
            const record = dailyRecordsRef.current[day];
            if (record && record._savedDate) {
                savedDatesByDay[day] = record._savedDate;
            }
        });
        
        Object.keys(dayRecords).forEach(dayStr => {
            const dayNumber = parseInt(dayStr);
            const recordsForDay = dayRecords[dayNumber];
            
            let record = null;
            
            // If we have a saved date for this day, try to match records by date first
            if (savedDatesByDay[dayNumber]) {
                const targetDate = parseLocalDate(savedDatesByDay[dayNumber]);
                if (!targetDate) {
                    // fall through to most-recent fallback below
                } else {
                
                // Find records that match the saved date (within same day)
                const matchingRecords = recordsForDay.filter(r => {
                    const recordDateStr = r.date || r.vital_sign_date || r.record_date || r.date_of_record || r.created_at;
                    const recordDate = parseLocalDate(recordDateStr);
                    if (!recordDate) return false;
                    return recordDate.getTime() === targetDate.getTime();
                });

                if (matchingRecords.length > 0) {
                    // Sort matching records by ID (descending) to get most recent
                    matchingRecords.sort((a, b) => (b.id || 0) - (a.id || 0));
                    record = matchingRecords[0];
                }
                }
            }
            
            // If no match by date, use most recent record (fallback)
            if (!record) {
                // Sort by ID (descending) or created_at (descending) to get most recent
                const sortedRecords = recordsForDay.sort((a, b) => {
                    // Prefer ID comparison (higher ID = more recent)
                    if (a.id && b.id) {
                        return b.id - a.id;
                    }
                    // Fallback to created_at
                    const dateA = new Date(a.created_at || a.date || 0);
                    const dateB = new Date(b.created_at || b.date || 0);
                    return dateB - dateA;
                });
                record = sortedRecords[0]; // Most recent record for this day
            }

            // Extract temperature, heart_rate, respiratory_rate from formatted strings
            // e.g., "38.5°C" -> 38.5, "120 bpm" -> 120, "30 breaths/min" -> 30
            const extractNumber = (str) => {
                if (!str) return '';
                const match = str.match(/[\d.]+/);
                return match ? match[0] : '';
            };

            const processedRecord = {
                id: record.id,
                // Ensure serial is always a string, never null or undefined
                serial: (record.serial === null || record.serial === undefined) 
                    ? '' 
                    : String(record.serial),
                foodIntake: record.food_intake || record.food || '',
                appetite: record.appetite || '',
                water: record.water || '',
                stool: record.stool || '',
                urine: record.urine || '',
                temp: extractNumber(record.temperature) || (record.temperature ? parseFloat(record.temperature) : '') || '',
                pulse: extractNumber(record.heart_rate) || (record.heart_rate ? parseInt(record.heart_rate) : '') || '',
                rr: extractNumber(record.respiratory_rate) || (record.respiratory_rate ? parseInt(record.respiratory_rate) : '') || '',
                crt: record.capillary_refill_time || '',
                mm: record.mucous_membrane_color || '',
                weight: record.body_weight ? parseFloat(record.body_weight) : '',
                hydration: record.hydration_status || '',
                bcs: record.body_condition_score || '',
                oralCavity: record.oral_cavity || '',
                lymphNodes: record.lymph_nodes || '',
                painScore: record.severity || record.pain_scale || '',
                physicalExamFindings: record.objective || record.physical_exam_findings || record.notes || ''
            };

            // If we already have a record for this day with a different ID, keep the most recent
            processed[dayNumber] = processedRecord;
        });
        
        // Merge with existing state to preserve any unsaved changes, but prioritize API data
        setDailyRecords(prev => {
            const merged = { ...prev };
            // Update with fetched data - API is source of truth
            Object.keys(processed).forEach(day => {
                merged[day] = processed[day];
            });
            
            return merged;
        });
        
        // Ref will be updated by the useEffect that syncs dailyRecords to dailyRecordsRef
    }, [visitDate]);

    const fetchVitalSigns = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/vital-signs/inpatient-visit/${inpatientId}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                    'Accept': 'application/json'
                }
            });

            if (isMounted.current && response.ok) {
                const result = await response.json();
                if (result.status === 'success' && Array.isArray(result.data)) {
                    processVitalSigns(result.data);
                }
            }
        } catch (error) {
            // ignore fetch errors
        } finally {
            if (isMounted.current) setLoading(false);
        }
    }, [inpatientId, processVitalSigns]);

    // Fetch vital signs on mount and when visitDate changes
    useEffect(() => {
        if (!inpatientId) return;
        fetchVitalSigns();
    }, [inpatientId, visitDate, fetchVitalSigns]);
    
    // Also create a ref to fetchVitalSigns so it can be called from save method
    const fetchVitalSignsRef = useRef(fetchVitalSigns);
    useEffect(() => {
        fetchVitalSignsRef.current = fetchVitalSigns;
    }, [fetchVitalSigns]);

    // Helper to extract local YYYY-MM-DD from either a string or Date
    const getLocalVisitDate = () => parseLocalDate(visitDate);

    useEffect(() => {
        const start = parseLocalDate(visitDate);
        if (!start) return;

        const today = parseLocalDate(new Date());
        const checkout = parseLocalDate(checkoutDate);

        let diffDays;
        if (isVisitOpen) {
            // Open visit: reveal one tab per day from admission up to today
            // (or up to a checkout date that already happened).
            const end = checkout && checkout < today ? checkout : today;
            diffDays = Math.max(0, Math.round((end - start) / (1000 * 60 * 60 * 24)));
            frozenDaysCountRef.current = null;
        } else if (checkout) {
            // Non-open with a known checkout: reveal the full stay up to checkout, frozen.
            const end = checkout < today ? checkout : today;
            diffDays = Math.max(0, Math.round((end - start) / (1000 * 60 * 60 * 24)));
        } else {
            // Non-open without a checkout date: freeze at whatever was revealed when
            // the visit stopped being open — never add new day tabs again.
            if (frozenDaysCountRef.current === null) {
                frozenDaysCountRef.current = Math.max(0, Math.round((today - start) / (1000 * 60 * 60 * 24)));
            }
            diffDays = frozenDaysCountRef.current;
        }

        const safeTotalDays = Number.isFinite(diffDays) ? diffDays + 1 : 1;
        const daysArray = Array.from({ length: safeTotalDays }, (_, i) => i + 1);
        setDays(daysArray);

        // Land on the latest day when the list is first computed (e.g. stay opened on
        // day 3) and whenever a new day appears while the modal is open (day rollover);
        // otherwise keep the user's selection so they can browse locked days freely.
        const selectedDate = parseLocalDate(getDayDate(selectedDayRef.current));
        const isSelectedToday = selectedDate && formatLocalDateKey(selectedDate) === todayKeyRef.current;
        if (!isSelectedToday || selectedDayRef.current > safeTotalDays) {
            setSelectedDay(safeTotalDays);
            selectedDayRef.current = safeTotalDays;
        }
    }, [visitDate, checkoutDate, todayKey, isVisitOpen]);

    const getDayDate = (dayIndex) => {
        const start = getLocalVisitDate();
        if (!start) return null;
        const date = new Date(start.getFullYear(), start.getMonth(), start.getDate());
        date.setDate(date.getDate() + (dayIndex - 1));
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    // Removed auto-fill logic - each day should start empty
    // Users can manually copy data if needed, but by default days should be empty

    const handleRecordChange = (field, value) => {
        setDailyRecords(prev => {
            const updated = {
                ...prev,
                [selectedDay]: {
                    ...(prev[selectedDay] || {}),
                    [field]: value
                }
            };
            
            // Update ref immediately with the new data
            dailyRecordsRef.current = updated;
            
            // Clear any existing timer for this day
            if (saveTimersRef.current[selectedDay]) {
                clearTimeout(saveTimersRef.current[selectedDay]);
            }

            // Set a new timer to save after 2 seconds (debounced)
            const timerId = setTimeout(() => {
                const recordToSave = dailyRecordsRef.current[selectedDay];
                if (recordToSave) {
                    saveDailyRecord(selectedDay, recordToSave);
                }
                delete saveTimersRef.current[selectedDay];
            }, 2000);

            saveTimersRef.current[selectedDay] = timerId;
            
            return updated;
        });
    };

    const handleBlur = () => {
        // Clear any pending timer and save immediately on blur
        if (saveTimersRef.current[selectedDay]) {
            clearTimeout(saveTimersRef.current[selectedDay]);
            delete saveTimersRef.current[selectedDay];
        }
        
        // Save immediately on blur
        const recordToSave = dailyRecordsRef.current[selectedDay];
        if (recordToSave) {
            saveDailyRecord(selectedDay, recordToSave);
        }
    };

    const currentRecord = dailyRecords[selectedDay] || {};

    const selectedDateStr = getDayDate(selectedDay);
    // Only today's tab is editable. Past days lock once their day is done, future
    // days don't exist yet. Non-open visits are fully frozen (no edits, no new tabs).
    const isTodaySelected = selectedDateStr && selectedDateStr === todayKey;
    const isEditable = !readOnly && isVisitOpen && isTodaySelected;

    if (loading && !Object.keys(dailyRecords).length) {
        return (
            <div className="daily-care-loading">
                <div className="spinner"></div>
                <p>{t('common.loading')}</p>
            </div>
        );
    }

    return (
        <div className="daily-care-content">
            <div className="days-tabs-container no-scrollbar">
                {days.map(day => {
                    const dayDateStr = getDayDate(day);
                    const dayEditable = !readOnly && isVisitOpen && dayDateStr === todayKey;
                    return (
                        <button
                            key={day}
                            className={`day-tab-btn ${selectedDay === day ? 'active' : ''} ${dayEditable ? '' : 'locked'}`}
                            onClick={() => setSelectedDay(day)}
                        >
                            <span className="tab-label">
                                {t('receptionInpatients.details.dailyCare.day', { count: day, defaultValue: `Day ${day}` })}
                                {!dayEditable && <i className="fas fa-lock tab-lock" aria-hidden="true"></i>}
                            </span>
                            <span className="tab-date">{dayDateStr}</span>
                        </button>
                    );
                })}
            </div>

            <div className="daily-care-form tab-content-inner">
                <div className="care-section">
                    <h4>{t('receptionInpatients.details.dailyCare.appetiteTreats', { defaultValue: 'Appetite Treats' })}</h4>
                    <div className="care-rows-container">
                        <div className="care-row">
                            <div className="care-field-group">
                                <label>{t('receptionInpatients.details.dailyCare.foodIntake', { defaultValue: 'Food Intake' })}</label>
                                <select
                                    value={currentRecord.foodIntake || ''}
                                    onChange={(e) => handleRecordChange('foodIntake', e.target.value)}
                                    className="care-select"
                                    disabled={!isEditable}
                                >
                                    <option value="">{t('common.select')}</option>
                                    <option value="Normal">{t('receptionInpatients.details.dailyCare.normal', { defaultValue: 'Normal' })}</option>
                                    <option value="Partial">{t('receptionInpatients.details.dailyCare.partial', { defaultValue: 'Partial' })}</option>
                                    <option value="None">{t('receptionInpatients.details.dailyCare.none', { defaultValue: 'None' })}</option>
                                    <option value="Assisted">{t('receptionInpatients.details.dailyCare.assisted', { defaultValue: 'Assisted' })}</option>
                                    <option value="NPO">{t('receptionInpatients.details.dailyCare.npo', { defaultValue: 'NPO' })}</option>
                                </select>
                            </div>

                            <div className="care-field-group">
                                <label>{t('receptionInpatients.details.dailyCare.appetite', { defaultValue: 'Appetite' })}</label>
                                <select
                                    value={currentRecord.appetite || ''}
                                    onChange={(e) => handleRecordChange('appetite', e.target.value)}
                                    className="care-select"
                                    disabled={!isEditable}
                                >
                                    <option value="">{t('common.select')}</option>
                                    <option value="Normal">{t('receptionInpatients.details.dailyCare.normal', { defaultValue: 'Normal' })}</option>
                                    <option value="Polyphagia">{t('receptionInpatients.details.dailyCare.polyphagia', { defaultValue: 'Polyphagia' })}</option>
                                    <option value="Hyporexia">{t('receptionInpatients.details.dailyCare.hyporexia', { defaultValue: 'Hyporexia' })}</option>
                                    <option value="Anorexia">{t('receptionInpatients.details.dailyCare.anorexia', { defaultValue: 'Anorexia' })}</option>
                                </select>
                            </div>

                            <div className="care-field-group">
                                <label>{t('receptionInpatients.details.dailyCare.water', { defaultValue: 'Water' })}</label>
                                <select
                                    value={currentRecord.water || ''}
                                    onChange={(e) => handleRecordChange('water', e.target.value)}
                                    className="care-select"
                                    disabled={!isEditable}
                                >
                                    <option value="">{t('common.select')}</option>
                                    <option value="Adequate">{t('receptionInpatients.details.dailyCare.adequate', { defaultValue: 'Adequate' })}</option>
                                    <option value="Reduced">{t('receptionInpatients.details.dailyCare.reduced', { defaultValue: 'Reduced' })}</option>
                                    <option value="None">{t('receptionInpatients.details.dailyCare.none', { defaultValue: 'None' })}</option>
                                    <option value="Excessive">{t('receptionInpatients.details.dailyCare.excessive', { defaultValue: 'Excessive' })}</option>
                                    <option value="IV fluids only">{t('receptionInpatients.details.dailyCare.ivFluidsOnly', { defaultValue: 'IV fluids only' })}</option>
                                </select>
                            </div>
                        </div>

                        <div className="care-row">
                            <div className="care-field-group">
                                <label>{t('receptionInpatients.details.dailyCare.stool', { defaultValue: 'Stool' })}</label>
                                <input
                                    type="text"
                                    className="care-input"
                                    value={currentRecord.stool || ''}
                                    onChange={(e) => handleRecordChange('stool', e.target.value)}
                                    placeholder={t('receptionInpatients.details.dailyCare.stoolPlaceholder', { defaultValue: 'e.g., Normal, Diarrhea, Constipated' })}
                                    disabled={!isEditable}
                                />
                            </div>

                            <div className="care-field-group">
                                <label>{t('receptionInpatients.details.dailyCare.urine', { defaultValue: 'Urine' })}</label>
                                <input
                                    type="text"
                                    className="care-input"
                                    value={currentRecord.urine || ''}
                                    onChange={(e) => handleRecordChange('urine', e.target.value)}
                                    placeholder={t('receptionInpatients.details.dailyCare.urinePlaceholder', { defaultValue: 'e.g., Normal, Hematuria, Polyuria' })}
                                    disabled={!isEditable}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="care-section">
                    <h4>{t('receptionInpatients.details.dailyCare.vitalSigns', { defaultValue: 'Vital Signs' })}</h4>
                    <div className="care-grid">
                            <div className="care-field-group">
                                <label>{t('receptionInpatients.details.dailyCare.temp', { defaultValue: 'Temp' })}</label>
                                <input
                                    type="text"
                                    className="care-input"
                                    value={currentRecord.temp || ''}
                                    onChange={(e) => handleRecordChange('temp', e.target.value)}
                                    placeholder="---"
                                    disabled={!isEditable}
                                />
                            </div>
                        <div className="care-field-group">
                            <label>{t('receptionInpatients.details.dailyCare.pulse', { defaultValue: 'Pulse' })}</label>
                            <input
                                type="text"
                                className="care-input"
                                value={currentRecord.pulse || ''}
                                onChange={(e) => handleRecordChange('pulse', e.target.value)}
                                placeholder="---"
                                disabled={!isEditable}
                            />
                        </div>
                        <div className="care-field-group">
                            <label>{t('receptionInpatients.details.dailyCare.rr', { defaultValue: 'RR' })}</label>
                            <input
                                type="text"
                                className="care-input"
                                value={currentRecord.rr || ''}
                                onChange={(e) => handleRecordChange('rr', e.target.value)}
                                placeholder="---"
                                disabled={!isEditable}
                            />
                        </div>
                        <div className="care-field-group">
                            <label>{t('receptionInpatients.details.dailyCare.crt', { defaultValue: 'CRT' })}</label>
                            <input
                                type="text"
                                className="care-input"
                                value={currentRecord.crt || ''}
                                onChange={(e) => handleRecordChange('crt', e.target.value)}
                                placeholder="---"
                                disabled={!isEditable}
                            />
                        </div>
                        <div className="care-field-group">
                            <label>{t('receptionInpatients.details.dailyCare.mm', { defaultValue: 'MM' })}</label>
                            <input
                                type="text"
                                className="care-input"
                                value={currentRecord.mm || ''}
                                onChange={(e) => handleRecordChange('mm', e.target.value)}
                                placeholder="---"
                                disabled={!isEditable}
                            />
                        </div>
                        <div className="care-field-group">
                            <label>{t('receptionInpatients.details.dailyCare.weight', { defaultValue: 'Weight' })}</label>
                            <input
                                type="text"
                                className="care-input"
                                value={currentRecord.weight || ''}
                                onChange={(e) => handleRecordChange('weight', e.target.value)}
                                placeholder={t('receptionInpatients.details.dailyCare.weightUnitKg', { defaultValue: 'kg' })}
                                disabled={!isEditable}
                            />
                        </div>
                        <div className="care-field-group">
                            <label>{t('receptionInpatients.details.dailyCare.hydration', { defaultValue: 'Hydration' })}</label>
                            <input
                                type="text"
                                className="care-input"
                                value={currentRecord.hydration || ''}
                                onChange={(e) => handleRecordChange('hydration', e.target.value)}
                                placeholder="---"
                                disabled={!isEditable}
                            />
                        </div>
                        <div className="care-field-group">
                            <label>{t('receptionInpatients.details.dailyCare.bcs', { defaultValue: 'BCS' })}</label>
                            <input
                                type="text"
                                className="care-input"
                                value={currentRecord.bcs || ''}
                                onChange={(e) => handleRecordChange('bcs', e.target.value)}
                                placeholder="---"
                                disabled={!isEditable}
                            />
                        </div>
                        <div className="care-field-group">
                            <label>{t('receptionInpatients.details.dailyCare.oralCavity', { defaultValue: 'Oral Cavity' })}</label>
                            <input
                                type="text"
                                className="care-input"
                                value={currentRecord.oralCavity || ''}
                                onChange={(e) => handleRecordChange('oralCavity', e.target.value)}
                                placeholder="---"
                                disabled={!isEditable}
                            />
                        </div>
                        <div className="care-field-group">
                            <label>{t('receptionInpatients.details.dailyCare.lymphNodes', { defaultValue: 'Lymph Nodes' })}</label>
                            <input
                                type="text"
                                className="care-input"
                                value={currentRecord.lymphNodes || ''}
                                onChange={(e) => handleRecordChange('lymphNodes', e.target.value)}
                                placeholder="---"
                                disabled={!isEditable}
                            />
                        </div>
                        <div className="care-field-group" style={{ gridColumn: 'span 2' }}>
                            <label>{t('receptionInpatients.details.dailyCare.painScore', { defaultValue: 'Pain Score' })}</label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                {['0', '1', '2', '3', '4'].map((level) => {
                                    const getLevelColors = (lvl) => {
                                        switch (lvl) {
                                            case '0': return { border: '#10B981', bg: '#ECFDF5', text: '#047857', label: '0' };
                                            case '1': return { border: '#FCD34D', bg: '#FFFBEB', text: '#B45309', label: '1' };
                                            case '2': return { border: '#F97316', bg: '#FFF7ED', text: '#9A3412', label: '2' };
                                            case '3': return { border: '#EF4444', bg: '#FEF2F2', text: '#B91C1C', label: '3' };
                                            case '4': return { border: '#991B1B', bg: '#FEF2F2', text: '#7F1D1D', label: '4' };
                                            default: return { border: '#E5E7EB', bg: '#FFFFFF', text: '#6B7280', label: lvl };
                                        }
                                    };
                                    const colors = getLevelColors(level);
                                    const isActive = String(currentRecord.painScore) === level;

                                    return (
                                        <div
                                            key={level}
                                            onClick={() => isEditable && handleRecordChange('painScore', level)}
                                            style={{
                                                padding: '6px 16px',
                                                borderRadius: '6px',
                                                border: '1px solid',
                                                borderColor: isActive ? colors.border : '#E2E8F0',
                                                backgroundColor: isActive ? colors.bg : (isEditable ? '#FFFFFF' : '#FAFAFA'),
                                                cursor: isEditable ? 'pointer' : 'default',
                                                fontSize: '0.9rem',
                                                fontWeight: 600,
                                                color: isActive ? colors.text : '#64748B',
                                                textAlign: 'center',
                                                minWidth: '40px',
                                                transition: 'all 0.2s',
                                                userSelect: 'none',
                                                opacity: !isEditable && !isActive ? 0.6 : 1
                                            }}
                                        >
                                            {level}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    <div className="care-section" style={{ marginTop: '24px' }}>
                        <h4>{t('receptionInpatients.details.dailyCare.physicalExamFindings', { defaultValue: 'Physical Exam Findings' })}</h4>
                        <div className="care-row">
                            <textarea
                                className="care-textarea"
                                value={currentRecord.physicalExamFindings || ''}
                                onChange={(e) => handleRecordChange('physicalExamFindings', e.target.value)}
                                onBlur={handleBlur}
                                placeholder={t('receptionInpatients.details.dailyCare.physicalExamFindingsPlaceholder', { defaultValue: 'Enter findings...' })}
                                disabled={!isEditable}
                                rows={6}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    border: '1px solid #e2e8f0',
                                    resize: 'vertical'
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default DailyCareTab;
